import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { AppConfigurationFragment } from "@saleor/fragments/types/AppConfigurationFragment";
import { maybe, renderCollection } from "@saleor/misc";
import { ListActions, ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colName: {
        width: 300
      },
      colProducts: {
        width: 160
      },
      colSubcategories: {
        width: 240
      },
      colTypeLevel: {
        width: 210
      }
    },
    colName: {
      paddingLeft: 0
    },
    colProducts: {
      textAlign: "center"
    },
    colSubcategories: {
      textAlign: "center"
    },
    collectionTbl: {
      "@media (max-width: 767px)": {
        width: "800px"
      }
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "AppConfigList" }
);

interface AppConfigListProps extends ListProps, ListActions {
  appConfigs?: AppConfigurationFragment[];
}

const numberOfColumns = 3;

const AppConfigList: React.FC<AppConfigListProps> = props => {
  const {
    appConfigs,
    disabled,
    settings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick
  } = props;

  const classes = useStyles(props);

  return (
    <ResponsiveTable className={classes.collectionTbl}>
      <TableHead>
        <TableCellHeader
          direction={undefined}
          arrowPosition="right"
          className={classes.colName}
        >
          <FormattedMessage defaultMessage="Name" />
        </TableCellHeader>
        <TableCellHeader className={classes.colTypeLevel}>
          <FormattedMessage
            defaultMessage="Date Updated"
            description="store type"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colTypeLevel}>
          <FormattedMessage
            defaultMessage="File Version"
            description="store type"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>

      <TableBody>
        {renderCollection(
          appConfigs,
          (appConfig, index) => (
            <TableRow
              className={classes.tableRow}
              hover={!!appConfig}
              onClick={appConfig ? onRowClick(appConfig.id) : undefined}
              key={appConfig ? appConfig.id : "skeleton"}
              data-test="id"
              data-test-id={maybe(() => appConfig.id)}
            >
              <TableCell
                className={classes.colName}
                data-test="name"
                id={`nameRow${index}`}
              >
                {appConfig && appConfig.name ? appConfig.name : <Skeleton />}
              </TableCell>
              <TableCell
                className={classes.colTypeLevel}
                data-test="dateUpdated"
                id={`dateUpdatedRow${index}`}
              >
                {appConfig && appConfig.updatedAt ? (
                  <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                    {appConfig?.updatedAt}
                  </Moment>
                ) : (
                  <Skeleton />
                )}
              </TableCell>
              <TableCell
                className={classes.colSubcategories}
                data-test="fileVersion"
                id={`fileVersionRow${index}`}
              >
                {appConfig && appConfig.version >= 0 ? (
                  `v${appConfig.version}`
                ) : (
                  <Skeleton />
                )}
              </TableCell>
            </TableRow>
          ),
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No application configurations found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

AppConfigList.displayName = "AppConfigList";
export default AppConfigList;
